import axios from 'axios';

// Base URL senza path: evita doppio /user_management/api se REACT_APP_API_URL è già la base API
const BASE = (process.env.REACT_APP_API_URL || process.env.REACT_APP_DOMAIN || '').replace(/\/user_management\/api\/?$/, '');
const API_URL = `${BASE}/user_management/api`;

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Dynamically attach Authorization header for each request
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    } else {
      delete config.headers.Authorization; // ensure old token is removed when logging out
    }
    return config;
  },
  (error) => Promise.reject(error)
);

/**
 * @param {Object} [params] - Filtri query: { search, role, company, primary_company, is_active }
 */
export const fetchUsers = async (params = {}) => {
  try {
    const queryParams = {};
    if (params.search != null && String(params.search).trim()) queryParams.search = params.search.trim();
    if (params.role != null && params.role !== '') queryParams.role = params.role;
    if (params.company != null && params.company !== '') queryParams.company = params.company;
    if (params.primary_company != null && params.primary_company !== '') queryParams.primary_company = params.primary_company;
    if (params.is_active !== undefined && params.is_active !== '') queryParams.is_active = params.is_active === true || params.is_active === 'true';
    const response = await api.get('/users/', { params: queryParams });
    return response.data;
  } catch (error) {
    console.error('Error fetching users:', error);
    throw error;
  }
};

export const createUser = async (userData) => {
  try {
    const response = await api.post('/users/', userData);
    return response.data;
  } catch (error) {
    console.error('Error creating user:', error);
    throw error;
  }
};

export const updateUser = async (userId, userData) => {
  try {
    const response = await api.put(`/users/${userId}/`, userData);
    return response.data;
  } catch (error) {
    console.error('Error updating user:', error);
    throw error;
  }
};

export const deleteUser = async (userId) => {
  try {
    await api.delete(`/users/${userId}/`);
  } catch (error) {
    console.error('Error deleting user:', error);
    throw error;
  }
};

export const fetchCurrentUserRole = async () => {
  try {
    const response = await api.get('/users/me/');  // Assuming you have an endpoint for the current user
    return response.data;
  } catch (error) {
    console.error('Error fetching current user role:', error);
    throw error;
  }
};

export const updateUserPassword = async (userId, newPassword) => {
  try {
    const response = await api.post(`/users/${userId}/set_password/`, { password: newPassword });
    return response.data;
  } catch (error) {
    console.error('Error updating password:', error);
    throw error;
  }
};

// New functions
export const fetchCompanies = async () => {
  try {
    const response = await api.get('/companies/');
    return response.data;
  } catch (error) {
    console.error('Error fetching companies:', error);
    throw error;
  }
};

export const fetchRoles = async (companyId) => {
  try {
    const url = companyId ? `/roles/?company=${companyId}` : '/roles/';
    const response = await api.get(url);
    return response.data;
  } catch (error) {
    console.error('Error fetching roles:', error);
    throw error;
  }
};

const fetchCarStockFields = async () => {
  try {
    const response = await api.get('/car-stock-fields/');
    return response.data
  } catch (error) {
    console.error('Error fetching CarStock fields:', error);
  }
};

const fetchDistinctValues = async (field) => {
  if (distinctValues[field]) return;
  try {
    const response = await api.get(`/api/car-stock-distinct-values/${field}/`);
    return response.data
  } catch (error) {
    console.error(`Error fetching distinct values for ${field}:`, error);
  }
};

export const fetchUsersByCompany = async (companyId) => {
  try {
    const response = await api.get(`/users/by-company/${companyId}/`);
    return response.data;
  } catch (error) {
    console.error('Error fetching users by company:', error);
    throw error;
  }
};
export const forgotPassword = async (email) => {
  try {
    const response = await api.post('/forgot-password/', { email });
    return response.data;
  } catch (error) {
    console.error('Error sending forgot password email:', error);
    throw error;
  }
};

export const resetPassword = async (uid, token, newPassword) => {
  try {
    const response = await api.post('/reset-password/', { uid, token, new_password: newPassword });
    return response.data;
  } catch (error) {
    console.error('Error resetting password:', error);
    throw error;
  }
};

export const uploadProfilePicture = async (userId, file) => {
  const formData = new FormData();
  formData.append('profile_picture', file);

  try {
    const response = await api.patch(`/users/${userId}/`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  } catch (error) {
    console.error('Error uploading profile picture:', error);
    throw error;
  }
};
